/**
 * Festival Family Display Names
 *
 * @deprecated This constant is deprecated and should not be used in application code.
 * Festival family display names are now stored in the database (festivals.festival_family_display_name).
 * The tRPC endpoint festival.getFilters returns display names from the database.
 *
 * This constant is kept for reference and migration purposes only.
 * Use the database values or the getFestivalFamilyDisplayName() function which provides fallback logic.
 *
 * For new festivals, set the festival_family_display_name when creating the festival record.
 */

export const FESTIVAL_FAMILY_DISPLAY_NAMES: Record<string, string> = {
	// Major Electronic Dance Music Brands
	amf: "AMF",
	arc: "ARC",
	edc: "EDC",
	"edc-orlando": "EDC Orlando",
	"edc-mexico": "EDC Mexico",
	ultra: "Ultra Music Festival",
	"ultra-miami": "Ultra Miami",
	"ultra-taiwan": "Ultra Taiwan",
	tomorrowland: "Tomorrowland",
	asot: "A State of Trance",
	"defqon-1": "Defqon.1",
	"crssd-festival": "CRSSD Festival",
	"iii-points": "III Points",
	ilesoniq: "ÎLESONIQ",
	circoloco: "CircoLoco",

	// Insomniac Events
	"beyond-wonderland": "Beyond Wonderland",
	dreamstate: "Dreamstate",
	"nocturnal-wonderland": "Nocturnal Wonderland",

	// Bass Music Festivals
	"bass-canyon": "Bass Canyon",
	"lost-lands": "Lost Lands",

	// Major Multi-Genre Festivals
	coachella: "Coachella",
	lollapalooza: "Lollapalooza",
	"outside-lands": "Outside Lands",
	portola: "Portola",
	m3f: "M3F",

	// NYE Events
	"countdown-nye": "Countdown NYE",
	"decadence-nye": "Decadence NYE",
	"proper-nye": "Proper NYE",
	"to-the-future-nye": "To The Future NYE",

	// Unique Formatting Cases
	"hard-summer": "HARD Summer",
	hulaween: "Hulaween",
	s2o: "S2O",
	"s2o-festival": "S2O Festival",

	// Other Major Festival Brands
	"warped-tour": "Warped Tour",
};

/**
 * Check if a festival family slug has a display name mapping
 */
export function hasFestivalFamilyDisplayName(slug: string): boolean {
	return slug in FESTIVAL_FAMILY_DISPLAY_NAMES;
}

/**
 * Get all mapped festival family slugs
 */
export function getMappedFestivalFamilies(): string[] {
	return Object.keys(FESTIVAL_FAMILY_DISPLAY_NAMES);
}

/**
 * Validate if festival families need display name mappings
 * Returns list of unmapped families that might benefit from manual mapping
 */
export function validateFestivalFamilyMappings(families: string[]): {
	mapped: string[];
	unmapped: string[];
	suggestions: Array<{ slug: string; suggested: string }>;
} {
	const mapped: string[] = [];
	const unmapped: string[] = [];
	const suggestions: Array<{ slug: string; suggested: string }> = [];

	for (const family of families) {
		if (hasFestivalFamilyDisplayName(family)) {
			mapped.push(family);
		} else {
			unmapped.push(family);

			// Generate suggestions for families that might need special formatting
			const suggestion = generateDisplayNameSuggestion(family);
			if (suggestion !== family) {
				suggestions.push({ slug: family, suggested: suggestion });
			}
		}
	}

	return { mapped, unmapped, suggestions };
}

/**
 * Internal helper to generate display name suggestions
 */
function generateDisplayNameSuggestion(slug: string): string {
	// Handle common patterns that might need special formatting
	const specialCases: Record<string, string> = {
		// Common abbreviations that should stay uppercase
		edc: "EDC",
		asot: "ASOT",
		hard: "HARD",
		dj: "DJ",
		usa: "USA",
		uk: "UK",

		// Common words that should be lowercase in titles
		and: "and",
		in: "in",
		of: "of",
		the: "the",
		at: "at",
		on: "on",
	};

	return slug
		.split("-")
		.map((word, index) => {
			const lower = word.toLowerCase();

			// First word should always be capitalized
			if (index === 0) {
				return (
					specialCases[lower] ||
					word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
				);
			}

			// Check for special cases
			if (specialCases[lower]) {
				return specialCases[lower];
			}

			// Default to title case
			return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
		})
		.join(" ");
}

/**
 * Get display name for a festival family
 * Compatible with mobile's original function
 */
export function getFestivalFamilyDisplayName(family: string): string {
	if (family === "all") return "All Festivals";
	if (family === "other") return "Other";
	return FESTIVAL_FAMILY_DISPLAY_NAMES[family] || generateDisplayNameSuggestion(family);
}